import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ChevronLeft } from 'lucide-react';
import { Button, Modal, ModalYesNo, RichTextEditor } from '@scripsteam/scrips-signal-ds';
import { buildSaveBody, useSaveMacro } from '@/settings/services';
import {
  formatDate,
  parseNoZIsoDatetime,
} from '@/shared/lib/date-format';
import type { MacroData, MacroHistory } from '@/types/settings-api';

/**
 * Macro history view + revert flow — PROD-764 PR-E.
 *
 * Renders the per-macro `macroHistories` list (already on the GET
 * response from PR-A, no new endpoint needed) and lets the user view
 * any past snapshot in a read-only Quill surface. From any non-most-
 * recent snapshot, a "Revert to this version" button triggers a
 * confirmation prompt that re-uses the existing PR-B save mutation
 * with the snapshot's `macroKey` / `macroValue` / `macroValueFormatted`
 * plus the current macro's `id`. No new endpoint.
 *
 * **Flutter source**: `macro_tab.dart:706-783` (`updatesHistoryView`)
 * + `macro_tab.dart:1100-1213` (the read-only QuillStaticView surface
 * with the Revert / Close buttons).
 *
 * **Why "no revert on most-recent"**: Flutter checks `index != 0` —
 * the macro is already at that version, so the button would be a
 * no-op + would cost the user a round trip. Hiding it matches the
 * Flutter UX exactly.
 *
 * **Revert wire**: `POST /api/Doctor/CreateUpdateMacro` with:
 *   - `id`: source macro's id (so the backend updates in-place, not
 *     creates a duplicate)
 *   - `macroKey` / `macroValue` / `macroValueFormatted`: the SNAPSHOT's
 *     fields (matches Flutter `macro_tab.dart:1174-1185`)
 *   - `isGlobal` / `ownerId` / `organizationId`: from the live macro,
 *     not the snapshot (revert restores content, not visibility/owner)
 *   - `macroCollaborators`: from the live macro (preserves the
 *     current sharing set; reverting content shouldn't unshare)
 *
 * No new POST shape. Pre-existing wire-shape tests cover the body.
 */
export interface MacroHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Source macro — provides id, ownerId, organizationId, the
   *  histories list, and the live collaborator set to preserve on
   *  revert. */
  macro: MacroData | null;
  /** providerId for cache invalidation routing on save. Same value
   *  the parent passes to MacroEditDialog. */
  providerId: string;
}

type View = 'list' | 'detail';

export function MacroHistoryDialog({
  open,
  onOpenChange,
  macro,
  providerId,
}: MacroHistoryDialogProps) {
  const [view, setView] = useState<View>('list');
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [confirmRevertOpen, setConfirmRevertOpen] = useState(false);
  const saveMutation = useSaveMacro(providerId);

  // Reset on (re)open. Mirrors the editor-dialog's open-effect pattern.
  useEffect(() => {
    if (open) {
      setView('list');
      setSelectedIndex(null);
      setConfirmRevertOpen(false);
    }
  }, [open]);

  const histories = useMemo(
    () => macro?.macroHistories ?? [],
    [macro?.macroHistories],
  );

  const selected: MacroHistory | null =
    selectedIndex !== null ? histories[selectedIndex] ?? null : null;

  // Flutter `index != 0` — the most-recent snapshot is already what
  // the macro shows; the revert button would be a no-op + cost a
  // round trip. Match the UX exactly.
  const isMostRecent = selectedIndex === 0;
  const showRevertButton = !!selected && !isMostRecent;

  const handleSelectHistory = (i: number) => {
    setSelectedIndex(i);
    setView('detail');
  };

  const handleBackToList = () => {
    setView('list');
    setSelectedIndex(null);
  };

  const handleRevert = () => {
    if (!macro || !selected) return;
    setConfirmRevertOpen(false);

    // Revert wire — preserve owner/org/visibility/collaborators from
    // the LIVE macro; restore content fields from the SNAPSHOT. Mirrors
    // Flutter `macro_tab.dart:1167-1194`.
    const body = buildSaveBody(
      // Hand a synthesised source through buildSaveBody so the
      // macroCollaborators list comes from the LIVE macro, not the
      // snapshot (the snapshot doesn't carry collaborators).
      macro,
      {
        macroKey: selected.macroKey,
        macroValue: selected.macroValue,
        macroValueFormatted: selected.macroValueFormatted,
        isGlobal: macro.isGlobal,
        isDeleted: false,
      },
    );

    saveMutation.mutate(body, {
      onSuccess: () => {
        toast.success(`Reverted to ${formatHistoryLabel(selected, true)}`);
        onOpenChange(false);
      },
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : 'Revert failed'),
    });
  };

  const initialDeltaOps = useMemo(() => {
    if (!selected?.macroValue) return [{ insert: '\n' }];
    try {
      const parsed = JSON.parse(selected.macroValue);
      return Array.isArray(parsed) ? parsed : [{ insert: '\n' }];
    } catch {
      return [{ insert: '\n' }];
    }
  }, [selected?.macroValue]);

  return (
    <>
      <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      title={<>{view === 'detail' && (
                <button
                  type="button"
                  onClick={handleBackToList}
                  aria-label="Back to history list"
                  className="icon-action"
                >
                  <ChevronLeft className="size-5" />
                </button>
              )}
              {view === 'list'
                ? `${macro?.macroKey?.trim() || 'Macro'} — history`
                : `Version: ${formatHistoryLabel(selected, false)}`}</>}
      size="xl"
      footer={<>{view === 'detail' && showRevertButton && (
              <Button
                type="button"
                onClick={() => setConfirmRevertOpen(true)}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending
                  ? 'Reverting…'
                  : 'Revert to this version'}
              </Button>
            )}
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={saveMutation.isPending}
            >
              Close
            </Button></>}
    >
          

          {view === 'list' ? (
            <div className="py-2 max-h-overlay-body-compact overflow-y-auto scrollbar-hide">
              {histories.length === 0 ? (
                <p className="text-sm text-text-muted py-6 text-center">
                  No history yet — macro hasn’t been edited since creation.
                </p>
              ) : (
                <ul data-slot="macro-history-list" className="divide-y divide-border-hairline">
                  {histories.map((h, i) => (
                    <li key={h.id || `${h.createdDate}-${i}`}>
                      <button
                        type="button"
                        onClick={() => handleSelectHistory(i)}
                        className="flex w-full items-center justify-between gap-3 px-3 py-3 text-left hover:bg-grey-100/50"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-text-default">
                            {i === 0 ? 'Last updated by' : 'Updated by'}{' '}
                            <span className="font-semibold">
                              {h.createdByName ||
                                macro?.ownerName ||
                                shortGuid(h.createdBy)}
                            </span>
                          </p>
                          <p className="mt-1 text-xs text-text-muted">
                            {formatHistoryDate(h.createdDate)}
                          </p>
                        </div>
                        <span className="text-xs font-medium text-brand-primary">
                          View
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <div className="py-2 max-h-overlay-body-compact overflow-y-auto scrollbar-hide">
              {/* Metadata strip */}
              <p className="text-sm text-text-muted mb-3">
                Updated by{' '}
                <span className="font-semibold text-text-default">
                  {selected?.createdByName ||
                    macro?.ownerName ||
                    (selected ? shortGuid(selected.createdBy) : '—')}
                </span>{' '}
                · {formatHistoryDate(selected?.createdDate)}
              </p>

              {/* Read-only rendering: the DS editor without caret or toolbar.
                  Force remount when the selected history changes — otherwise
                  Quill caches the previous snapshot's Delta and the user briefly
                  sees stale content before the value applies. */}
              <div data-slot="settings-rich-box">
                <RichTextEditor
                  key={`history-${selected?.id ?? selectedIndex}`}
                  className="rounded-md border border-border-hairline-strong bg-surface-card"
                  aria-label="Macro snapshot"
                  readOnly
                  toolbar={false}
                  value={{ ops: initialDeltaOps }}
                />
              </div>
            </div>
          )}

          
        </Modal>

      {/* Confirm revert — matches Flutter's `confirmDialog` with a
          message that names the snapshot's macroKey. */}
      <ModalYesNo
      open={confirmRevertOpen}
      onCancel={() => setConfirmRevertOpen(false)}
      title="Revert macro to this version"
      message={<>Are you sure you want to revert macro to this version{' '}
              <span className="font-semibold">
                {selected?.macroKey || ''}
              </span>
              ?</>}
      cancelLabel="No"
      confirmLabel="Yes"
      onConfirm={handleRevert}
    />
    </>
  );
}

function formatHistoryDate(iso: string | undefined | null): string {
  if (!iso) return '—';
  const dt = parseNoZIsoDatetime(iso);
  return dt ? formatDate(dt) : '—';
}

function formatHistoryLabel(
  h: MacroHistory | null,
  withDate: boolean,
): string {
  if (!h) return '—';
  if (withDate) {
    return `${h.macroKey || 'this version'} (${formatHistoryDate(h.createdDate)})`;
  }
  return h.macroKey || formatHistoryDate(h.createdDate);
}

function shortGuid(s: string | null | undefined): string {
  if (!s) return '—';
  return s.length > 8 ? `${s.slice(0, 8)}…` : s;
}
